import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import apiConstants from "../../constants/apiConstants";
import {
  Stack,
  Stepper,
  styled,
  Step,
  StepLabel,
  Typography,
  DialogTitle,
  Dialog,
  IconButton,
  CloseIcon,
  DialogContent,
  CheckIcon,
  WarningIcon,
  Slide,
} from "../../styles/MaterialUI";
import SettingsIcon from "@mui/icons-material/Settings";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import PropTypes from "prop-types";
import { ThreeDots } from "react-loader-spinner";
import { useDispatch, useSelector } from "react-redux";
import { showAlertPopup } from "../Redux/Features/showAlertSlice";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ContentProgress = ({ openProgress, onClose, contentId, contentType, screenData }) => {
  const dispatch = useDispatch();
  const { userToken } = useSelector((state) => state.userToken);
  const [stageData, setStageData] = useState([]);
  const [loader, setLoader] = useState(true);
  const headers = { Authorization: `Bearer ${userToken}` };
  const hasShownAlertRef = useRef(false); // tracks if alert is already shown

  useEffect(() => {
    if (!Array.isArray(stageData) || stageData.length === 0) return;

    const allStatusesComplete = stageData.every(item => item?.stage3?.status === "Complete");

    if (allStatusesComplete && !hasShownAlertRef.current) {
      hasShownAlertRef.current = true; // prevent future alerts
      dispatch(showAlertPopup({ isOpen: true, severity: "success", message: "Content Mapped Successfully" }));
    }

    // Reset the flag if any status becomes incomplete again
    if (!allStatusesComplete) {
      hasShownAlertRef.current = false;
    }
  }, [stageData]);

  const formatDate = (input) => {
    if (input) {
      const newDate = new Date(input);
      return newDate.toLocaleString();
    }
    return input;
  }

  const extractScreenUiid = (data) => {
    let ids = [];
    if (!data) return [];

    if (Array.isArray(data)) {
      data.forEach(item => {
        if (item.id) {
          ids.push(item.id);
        }
      });
    } else if (typeof data === 'object' && data !== null && Array.isArray(data.id)) {
      ids = data.id;
    }
    return ids;
  }

  const getDashboardRequests = async () => {
    const screenUiid = extractScreenUiid(screenData);
    try {
      const response = await axios.post(
        `${apiConstants.GET_API_URL}getContentRequests`,
        { content_id: contentId, screen_uiids: screenUiid },
        { headers }
      );

      const { data } = response.data;

      const output = data.map(item => {
        const completedCount = Object.values(item).filter(value => value === "Complete").length;
        return {
          completed: completedCount,
          screen_name: item.screen_name,
          ic_device_screen_status: item.ic_device_screen_status,
          content_type: "", content_name: "",
          stage0: { status: "", updatedOn: formatDate(item.ic_requestedon) },
          stage1: { status: item.ic_Stage1Status, updatedOn: formatDate(item.ic_Stage1UpdatedOn) },
          stage2: { status: item.ic_Stage2Status, updatedOn: formatDate(item.ic_Stage2UpdatedOn) },
          stage3: { status: item.ic_Stage3Status, updatedOn: formatDate(item.ic_Stage3UpdatedOn) }
        };
      });

      setStageData(output);
      setLoader(false);
    } catch (error) {
      const { statusCode, message, redirect_url } = error.response.data;
      dispatch(showAlertPopup({ isOpen: true, severity: "error", message: message }));
      if (statusCode === 403) {
        window.open(redirect_url, "_self");
      }
    }
  };

  const getImageRequests = async () => {
    const screenUiid = extractScreenUiid(screenData);
    try {
      const response = await axios.post(
        `${apiConstants.GET_API_URL}getFileRequests`,
        { content_id: contentId, screen_uiids: screenUiid },
        { headers }
      );

      const { data } = response.data;

      const output = data.map(item => {
        const completedCount = (item.ic_device_screen_status === 0 && item.ic_Stage3Status !== "Complete") ? 0 : Object.values(item).filter(value => value === "Complete").length;
        return {
          completed: completedCount,
          screen_name: item.screen_name,
          ic_device_screen_status: item.ic_device_screen_status,
          content_type: "", content_name: "",
          stage0: { status: "", updatedOn: formatDate(item.ic_requestedon) },
          stage1: { status: item.ic_Stage1Status, updatedOn: formatDate(item.ic_Stage1UpdatedOn) },
          stage2: { status: item.ic_Stage2Status, updatedOn: formatDate(item.ic_Stage2UpdatedOn) },
          stage3: { status: item.ic_Stage3Status, updatedOn: formatDate(item.ic_Stage3UpdatedOn) }
        };
      });

      setStageData(output);
      setLoader(false);
    } catch (error) {
      const { statusCode, message, redirect_url } = error.response.data;
      dispatch(showAlertPopup({ isOpen: true, severity: "error", message: message }));
      if (statusCode === 403) {
        window.open(redirect_url, "_self");
      }
    }
  };

  const getPlaylistRequests = async () => {
    const screenUiid = extractScreenUiid(screenData);
    try {
      const response = await axios.post(
        `${apiConstants.GET_API_URL}getPlaylistRequests`,
        { content_id: contentId, screen_uiids: screenUiid },
        { headers }
      );
      const { data } = response.data;

      const playlistData = data.flatMap(playlist => (
        (playlist.fileRequests || []).map(request => {
          const completedCount = request.content_type === "dashboard" ? Object.values(request).filter(value => value === "Complete").length
            : (request.ic_device_screen_status === 0 && request.ic_Stage3Status !== "Complete") ? 0 : Object.values(request).filter(value => value === "Complete").length;
          return ({
            completed: completedCount,
            screen_name: request.screen_name,
            ic_device_screen_status: request.ic_device_screen_status,
            content_type: request.content_type,
            content_name: request.content_name,
            stage0: { status: "", updatedOn: formatDate(request.ic_requestedon) },
            stage1: { status: request.ic_Stage1Status, updatedOn: formatDate(request.ic_Stage1UpdatedOn) },
            stage2: { status: request.ic_Stage2Status, updatedOn: formatDate(request.ic_Stage2UpdatedOn) },
            stage3: { status: request.ic_Stage3Status, updatedOn: formatDate(request.ic_Stage3UpdatedOn) }
          })
        })
      ));

      setStageData(playlistData);
      setLoader(false);
    } catch (error) {
      const { statusCode, message, redirect_url } = error.response.data;
      dispatch(showAlertPopup({ isOpen: true, severity: "error", message: message }));
      if (statusCode === 403) {
        window.open(redirect_url, "_self");
      }
    }
  };

  useEffect(() => {
    let intervalId;

    const fetchData = () => {
      if (openProgress) {
        intervalId = setInterval(() => {
          if (contentType === "Image" || contentType === "Video" || contentType === "Machine Metrics" || contentType === "Acumatica" || contentType === "Open URL") {
            getImageRequests();
          } else if (contentType === "IntelliDash Dashboard") {
            getDashboardRequests();
          } else if (contentType === "Content Playlist") {
            getPlaylistRequests();
          }
        }, 5000);
      }
    }

    fetchData();

    return () => clearInterval(intervalId);
  }, [openProgress, contentType]);

  const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 20,
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: "#ff8000",
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: "green",
      },
    },
    [`&.${stepConnectorClasses.error}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: "red",
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: 3,
      border: 0,
      backgroundColor:
        theme.palette.mode === "dark" ? theme.palette.grey[800] : "#eaeaf0",
      borderRadius: 1,
    },
  }));

  const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.grey[700] : "#ccc",
    zIndex: 1,
    color: "#fff",
    width: 40,
    height: 40,
    display: "flex",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
    ...(ownerState.active && {
      backgroundColor: "#ff8000",
      boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
      animation: "$rotate 1.25s linear infinite",
      animationName: "rotate",
      animationDuration: "1.25s",
      animationTimingFunction: "linear",
      animationIterationCount: "infinite",
    }),
    "@keyframes rotate": {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" }
    },

    ...(ownerState.completed && {
      backgroundColor: "green",
      animationName: "none",
    }),
    ...(ownerState.error && {
      backgroundColor: "red",
      animationName: "none",
    }),
  }));

  function ColorlibStepIcon(props) {
    const { active, completed, className, error } = props;
    return (
      <ColorlibStepIconRoot
        ownerState={{ completed, active, error }}
        className={className}
      >
        {completed ? <CheckIcon /> : error ? <WarningIcon /> : <SettingsIcon />}
      </ColorlibStepIconRoot>
    );
  }

  ColorlibStepIcon.propTypes = {
    /**
     * Whether this step is active.
     * @default false
     */
    active: PropTypes.bool,
    className: PropTypes.string,
    /**
     * Mark the step as completed. Is passed to child components.
     * @default false
     */
    completed: PropTypes.bool,
    error: PropTypes.bool,
  };

  const steps = (contentType === "Image" || contentType === "Video" || contentType === "Machine Metrics" || contentType === "Acumatica" || contentType === "Open URL") ?
    ["Request Initiated", "Encrypting Data", "Content Transmission", "Transfer Completed"] :
    ["Request Initiated", "Content Initialization", "Content Generation / Transmission", "Transfer Completed"];

  const stageTitle = {
    "dp0": "Request Initiated", "dp1": "Content Initialization", "dp2": "Content Generation / Transmission", "dp3": "Transfer Completed",
    "iv0": "Request Initiated", "iv1": "Encrypting Data", "iv2": "Content Transmission", "iv3": "Transfer Completed",
  };

  const stage = [" ", "Stage 1", "Stage 2", "Stage 3"];

  const handleCloseDialog = (event, reason) => {
    if (reason !== "backdropClick") {
      onClose();
    }
  }

  return (
    <>
      <Dialog open={openProgress} onClose={handleCloseDialog} fullWidth maxWidth="lg" disableEscapeKeyDown TransitionComponent={Transition}>
        <DialogTitle sx={{ m: 0, p: 1, pl: 2, pr: 2, color: "white", backgroundColor: "#003366", }}>
          Content Progress
          <IconButton aria-label="close" onClick={onClose} style={{ float: "right", color: "gray" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" display="flex" justifyContent="center" style={{ marginTop: "5px" }}>
            <ThreeDots visible={loader} height="80" width="80" color="#003366" radius="9" ariaLabel="three-dots-loading" />
          </Stack>
          <Stack sx={{ width: "100%", marginTop: "10px" }} spacing={4}>
            {stageData.map((content, i) => (
              <Stepper
                alternativeLabel
                activeStep={content.completed + 1}
                connector={<ColorlibConnector />}
                key={i}
              >
                {steps.map((label, index) => {
                  return (
                    <Step key={label}>
                      <StepLabel StepIconComponent={ColorlibStepIcon} error={
                        ((index === 1 && content.stage1.status === "FAILED") ||
                          (index === 2 && content.stage2.status === "FAILED") ||
                          (index === 3 && content.stage3.status === "FAILED") ||
                          (index === 1 && (contentType === "Image" || contentType === "Video" || contentType === "Machine Metrics" || contentType === "Acumatica" || contentType === "Open URL") && content.completed === 0 && content.ic_device_screen_status === 0) ||
                          (index === 1 && contentType === "Content Playlist" && (content.content_type === "image" || content.content_type === "video" || content.content_type === "orchestrated_url" || content.content_type === "open_url") && content.completed === 0 && content.ic_device_screen_status === 0) ||
                          (index === 3 && contentType === "IntelliDash Dashboard" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0) ||
                          // (index === 3 && contentType === "Machine Metrics" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0) ||
                          (index === 3 && contentType === "Content Playlist" && content.content_type === "dashboard" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0)) ? true : false
                      }>
                        {contentType === "Content Playlist" ? content.content_type === "dashboard" ? stageTitle[`dp${index}`] : stageTitle[`iv${index}`] : label}
                      </StepLabel>
                      <Typography
                        style={{
                          textAlign: "center", fontSize: "12px", fontWeight: 'bold',
                          color: ((index === 1 && content.stage1.status === "FAILED") ||
                            (index === 2 && content.stage2.status === "FAILED") ||
                            (index === 3 && content.stage3.status === "FAILED") ||
                            (index === 1 && (contentType === "Image" || contentType === "Video" || contentType === "Machine Metrics" || contentType === "Acumatica" || contentType === "Open URL") && content.completed === 0 && content.ic_device_screen_status === 0) ||
                            (index === 1 && contentType === "Content Playlist" && (content.content_type === "image" || content.content_type === "video" || content.content_type === "orchestrated_url" || content.content_type === "open_url") && content.completed === 0 && content.ic_device_screen_status === 0) ||
                            (index === 3 && contentType === "IntelliDash Dashboard" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0) ||
                            //(index === 3 && contentType === "Machine Metrics" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0) ||
                            (index === 3 && contentType === "Content Playlist" && content.content_type === "dashboard" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0)) ? 'red' : 'inherit',
                        }}
                      >
                        {index === 0 ? content.screen_name : stage[index]}
                      </Typography>
                      <Typography
                        style={{
                          textAlign: "center", fontSize: "12px",
                          color: ((index === 1 && content.stage1.status === "FAILED") ||
                            (index === 2 && content.stage2.status === "FAILED") ||
                            (index === 3 && content.stage3.status === "FAILED") ||
                            (index === 1 && (contentType === "Image" || contentType === "Video" || contentType === "Machine Metrics" || contentType === "Acumatica" || contentType === "Open URL") && content.completed === 0 && content.ic_device_screen_status === 0) ||
                            (index === 1 && contentType === "Content Playlist" && (content.content_type === "image" || content.content_type === "video" || content.content_type === "orchestrated_url" || content.content_type === "open_url") && content.completed === 0 && content.ic_device_screen_status === 0) ||
                            (index === 3 && contentType === "IntelliDash Dashboard" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0) ||
                            // (index === 3 && contentType === "Machine Metrics" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0) ||
                            (index === 3 && contentType === "Content Playlist" && content.content_type === "dashboard" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0)) ? 'red' : 'inherit',
                        }}
                      >
                        {index === 0 && content.content_name}
                      </Typography>
                      <Typography style={{
                        textAlign: 'center', fontSize: '12px',
                        color: ((index === 1 && content.stage1.status === "FAILED") ||
                          (index === 2 && content.stage2.status === "FAILED") ||
                          (index === 3 && content.stage3.status === "FAILED") ||
                          (index === 1 && (contentType === "Image" || contentType === "Video" || contentType === "Machine Metrics" || contentType === "Acumatica" || contentType === "Open URL") && content.completed === 0 && content.ic_device_screen_status === 0) ||
                          (index === 1 && contentType === "Content Playlist" && (content.content_type === "image" || content.content_type === "video" || content.content_type === "orchestrated_url" || content.content_type === "open_url") && content.completed === 0 && content.ic_device_screen_status === 0) ||
                          (index === 3 && contentType === "IntelliDash Dashboard" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0) ||
                          //(index === 3 && contentType === "Machine Metrics" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0) ||
                          (index === 3 && contentType === "Content Playlist" && content.content_type === "dashboard" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0)) ? 'red' : 'inherit',
                      }}>
                        {index === 0 && `INITIATED at ${content.stage0.updatedOn}`}
                        {index === 1 && content.stage1.status === "Complete" && content.completed !== 0 && `COMPLETED at ${content.stage1.updatedOn}`}
                        {index === 1 && content.stage1.status === "FAILED" && `FAILED at ${content.stage1.updatedOn}`}
                        {index === 1 && content.stage1.status === "CANCELLED" && `CANCELLED at ${content.stage1.updatedOn}`}
                        {index === 1 && (contentType === "Image" || contentType === "Video" || contentType === "Machine Metrics" || contentType === "Acumatica" || contentType === "Open URL") && content.completed === 0 && content.ic_device_screen_status === 0 && `Screen Offline`}
                        {index === 1 && contentType === "Content Playlist" && (content.content_type === "image" || content.content_type === "video" || content.content_type === "orchestrated_url" || content.content_type === "open_url") && content.completed === 0 && content.ic_device_screen_status === 0 && `Screen Offline`}
                        {index === 2 && content.stage2.status === "Complete" && content.completed !== 0 && `COMPLETED at ${content.stage2.updatedOn}`}
                        {index === 2 && content.stage2.status === "FAILED" && `FAILED at ${content.stage2.updatedOn}`}
                        {index === 2 && content.stage2.status === "CANCELLED" && `CANCELLED at ${content.stage2.updatedOn}`}
                        {index === 3 && content.stage3.status === "Complete" && content.completed !== 0 && `COMPLETED at ${content.stage3.updatedOn}`}
                        {index === 3 && content.stage3.status === "FAILED" && `FAILED at ${content.stage3.updatedOn}`}
                        {index === 3 && content.stage3.status === "CANCELLED" && `CANCELLED at ${content.stage3.updatedOn}`}
                        {index === 3 && contentType === "IntelliDash Dashboard" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0 && `Screen Offline`}
                        {/* {index === 3 && contentType === "Machine Metrics" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0 && `Screen Offline`} */}
                        {index === 3 && contentType === "Content Playlist" && content.content_type === "dashboard" && content.stage2.status === "Complete" && content.stage3.status !== "Complete" && content.ic_device_screen_status === 0 && `Screen Offline`}
                      </Typography>
                    </Step>
                  )
                })}
              </Stepper>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContentProgress;
